import { createTheme, ThemeProvider } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Typography,
    Button,
    Grid,
    ListItem,
    List,
    CircularProgress,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Avatar,
    Snackbar,
    Alert,
    Tooltip,
} from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ColorConstants from '../../defaults/ColorConstants';
import FieldNames from '../../defaults/FieldNames.json';
import GridDefinitions from '../../defaults/GridDefinitions.json';
import '../list/TreatmentActivityList/TreatmentActivityList.css';
import './DetailsContainer.css';
import fieldsDefinition from './FieldInfo.json';
import FormGroupings from './FormGroupings.json';
import DetailsGridProjectItem from './customPanels/DetailsGridProjectItem';
import DetailsProjectHeader from './DetailsProjectHeader';
import { setFilterGeometry, setIsLocationFilterOnly } from '../../redux/slices/filterSlice';
import { useDispatch, useSelector } from 'react-redux';
import FeatureService from '../../services/FeatureService';
import AuditLogService from '../../services/AuditLogService';
import { formatDateForDisplay, getSaveableDate } from '../../utils/DateHelper';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { emitCustomEvent, useCustomEventListener } from 'react-custom-events';
import Events from '../../defaults/Events';
import ErrorService from '../../services/ErrorService';
import ProjectService from '../../services/ProjectService';
import IDService from '../../services/IDService';
import {
    genericProjectValidationFieldSetFalse,
    genericProjectValidationFieldSetTrue,
    resetProjectValidationState,
} from '../../redux/slices/projectValidationSlice';
import {
    getAllMissingFields,
    getRequiredFields,
    isFeaturePostFire,
    isFeatureTreatment,
} from '../../utils/FeatureHelper';
import DetailsEdited from './DetailsEdited';
import DetailsRequired from './DetailsRequired';
import { getLogForFieldUpdatesOnExistingFeature } from '../../utils/AuditLogHelper';
import { setShowGlobalLoader } from '@store/slices/globalContextSlice';
import { setIsCreatingPostFire, setIsCreatingPlannedFeature } from '../../redux/slices/displayStateSlice';
import { setIsCreatingPostFireProject } from '../../redux/slices/displayStateSlice';
import { setShowMap } from '../../redux/slices/displayStateSlice';
import { setIsNewFeature } from '../../redux/slices/displayStateSlice';
import {setFeature, setIsTreatment, setMode, setProject} from '../../redux/slices/detailsSlice';
import { createStyledDrawer } from '../shared/StyledDrawer';
import {handleEstablishGridColumns, handleExpandedChanged} from "../shared/DetailsSharedLogic";
import {selectProject} from "../../utils/ReduxHelper";

const Drawer = createStyledDrawer(650);

const theme = createTheme({
    overrides: {
        MuiSnackbar: {
            root: {
                top: '94px !important',
            },
        },
    },
});

const useStyles = makeStyles(() => ({
    snackBarStyle: {
        top: '120px !important',
    },
}));

//TODO: Break out Snackbar into own control, make more obvious

export default function DetailsProject({
                               }) {
    const navigate = useNavigate();

    const dispatch = useDispatch();
    const userInfo = useSelector((state) => state.globalContext.userInfo);

    const projectValidationUnsavedValues = useSelector((state) => {
        return state.projectValidation;
    });
    const isNewFeature = useSelector((state) => state.displayState.isNewFeature);
    const feature = useSelector((state) => state.details.feature);
    const project = useSelector(selectProject);

    const mode = useSelector((state) => state.details.mode);
    const isProject = useSelector((state) => state.details.isProject);

    const classes = useStyles();

    const [groupDetails] = useState(FormGroupings);
    const [expandedItems, setExpandedItems] = useState([]);
    const [featureHistoryRows, setFeatureHistoryRows] = useState([]);
    const [featureHistoryCols, setFeatureHistoryCols] = useState([]);
    const [projectLinkedFeatures, setProjectLinkedFeatures] = useState([]);
    const [isLinkedQuerying, setIsLinkedQuerying] = useState(false);
    const [disableEdit, setDisableEdit] = useState(false);
    const [disableEditMessage, setDisableEditMessage] = useState(false);
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);
    const [baseProjectAttributes, setBaseProjectAttributes] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [saveDisabledMessage, setSaveDisabledMessage] = useState('');
    const [isNameVerified, setIsNameVerified] = useState('');
    const [requiredFields, setRequiredFields] = useState(FieldNames.projectRequiredFields);

    useCustomEventListener(Events.TriggerFieldChange, (data) => {
        if (data) {
            onHandleAttributeChanged(data.field, data.value);
        }
    });

    useEffect(() => {
        handleEstablishGridColumns(setFeatureHistoryCols);
        dispatch(setIsTreatment(false))
    }, []);

    useEffect(() => {
        setIsLinkedQuerying(true);
        if (baseProjectAttributes === null && project?.attributes) {
            setBaseProjectAttributes(_.cloneDeep(project.attributes));
            dispatch(resetProjectValidationState());
        }
        if (project?.attributes[FieldNames.projectFields.id]) {
            FeatureService.getLinkedFeaturesByProjectID(
                project.attributes[FieldNames.projectFields.id]
            ).then((linkedFeatures) => {
                setProjectLinkedFeatures(linkedFeatures);
                setIsLinkedQuerying(false);
                AuditLogService.getEntityAuditLog(
                    FieldNames.joinFeatureValues.project,
                    project?.attributes[FieldNames.projectFields.id]
                ).then((auditRows) => {
                    if (auditRows && auditRows.length > 0) {
                        const mappedRows = auditRows.map((auditRow) => {
                            try {
                                return {
                                    id: auditRow.OBJECTID,
                                    ModifiedBy: auditRow.ModifiedBy,
                                    ModifiedDate: formatDateForDisplay(auditRow.ModifiedDate),
                                    Modification: JSON.parse(auditRow.AuditEntryJson)[0],
                                };
                            } catch (e) {
                                return {
                                    id: auditRow.OBJECTID,
                                    ModifiedBy: auditRow.ModifiedBy,
                                    ModifiedDate: formatDateForDisplay(auditRow.ModifiedDate),
                                    Modification: auditRow.AuditEntryJson,
                                };
                            }
                        });
                        setFeatureHistoryRows(mappedRows);
                    } else {
                        setFeatureHistoryRows([]);
                    }
                });
            });
        } else {
            setIsLinkedQuerying(false);
        }
    }, [project, baseProjectAttributes]);

    useEffect(() => {
        if (isNewFeature && !project?.attributes?.[FieldNames.projectFields.name]) {
            setIsSaveDisabled(true);
            setSaveDisabledMessage('Select a name before saving');
        }
    }, [isNewFeature, project]);

    useEffect(() => {
        if (isNewFeature) {
            setIsNameVerified(FieldNames.yesNo.no);
        } else {
            setIsNameVerified(FieldNames.yesNo.yes);
        }
    }, [isNewFeature, setIsNameVerified]);

    useEffect(() => {
        let tempFeature = project ? Object.assign({}, project) : null;
        if (tempFeature) {
            const currentfieldInfo = fieldsDefinition[FieldNames.featureFields.class];
            const domain = currentfieldInfo?.domain;
            if (domain) {
                const codedValues = domain.codedValues;
                if (codedValues?.length > 0 && tempFeature?.attributes) {
                    tempFeature.attributes[FieldNames.featureFields.class] = codedValues[0].code;
                }
            }
        }
    }, [project]);

    useEffect(() => {
        let obj = {};
        let allIds = groupDetails.projectDetails.map((element) => {
            return element.identifier;
        });
        allIds.forEach((id) => {
            obj[id] = 'true';
        });
        setExpandedItems(obj);
    }, [groupDetails]);

    const title = (detail) => {
        return (
            <div className='incident-details-panel-header'>
                <h4>{detail.title}</h4>
            </div>
        );
    };

    const onClose = () => {
        dispatch(setMode(FieldNames.modes.read));
        emitCustomEvent(Events.DisableDrawMode);
        emitCustomEvent(Events.DisableExistingIcons, true);
        emitCustomEvent(Events.DisableNewActualIcons, true);
        emitCustomEvent(Events.DisableNewEstimatedIcons, false);
        dispatch(setProject(null));
        navigate('/home');
    };

    const onHandleEditClicked = () => {
        dispatch(setMode(FieldNames.modes.edit));
    };

    const handleCreatePostFireTreatment = () => {
        dispatch(setShowMap(true));
        dispatch(setIsCreatingPlannedFeature(true));
        dispatch(setIsCreatingPostFire(true));
        dispatch(setIsTreatment(true));
        dispatch(setIsCreatingPostFireProject(false));
    };

    const handleCreatePostFireActivity = () => {
        dispatch(setShowMap(true));
        dispatch(setIsCreatingPlannedFeature(true));
        dispatch(setIsCreatingPostFire(true));
        dispatch(setIsTreatment(false));
        dispatch(setIsCreatingPostFireProject(false));
    };

    const verifyName = () => {
        //Query all projects to see if the name already exists
        if (project?.attributes?.[FieldNames.projectFields.name]) {
            ProjectService.getIsProjectNameExisting(
                project?.attributes?.[FieldNames.projectFields.name],
                project?.attributes?.[FieldNames.projectFields.objectID]
            ).then((projectCountWithName) => {
                if (projectCountWithName === 0) {
                    setIsSaveDisabled(false);
                    setIsNameVerified(FieldNames.yesNo.yes);
                    setSaveDisabledMessage('');
                } else {
                    setIsSaveDisabled(true);
                    setIsNameVerified(FieldNames.yesNo.no);
                    setSaveDisabledMessage(
                        `A Project in IFPRS already has this name. Please verify that the already named project is not a duplicate, if it is not please enter a unique name. (Ex. Bear Hollow FY25)`
                    );
                    setSnackbarMessage(
                        `A Project in IFPRS already has this name. Please verify that the already named project is not a duplicate, if it is not please enter a unique name. (Ex. Bear Hollow FY25)`
                    );
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                }
            });
        } else {
            setIsSaveDisabled(true);
            setIsNameVerified(FieldNames.yesNo.no);
            setSaveDisabledMessage('Project must have a name before saving');
        }
    };

    const handleNewPostFireTreatmentAction = () => {
        dispatch(setIsLocationFilterOnly(false));
        dispatch(setFilterGeometry(null));
        handleCreatePostFireTreatment();
    };

    const handleNewPostFireActivityAction = () => {
        dispatch(setIsLocationFilterOnly(false));
        dispatch(setFilterGeometry(null));
        handleCreatePostFireActivity();
    };

    const handleClickOfLinkedAction = (eaAction) => {
        let url = '';
        const isEntityTreatment = isFeatureTreatment(false, eaAction, FieldNames.featureFields);
        const isPostFire = isFeaturePostFire(eaAction, false);
        const entityString = isEntityTreatment ? 'treatments' : 'activities';
        const entityTypeString = isPostFire ? 'pf' : 'f';
        url = `/${entityTypeString}/${entityString}/${eaAction[FieldNames.featureFields.objectID]}`;
        return url;
    };

    const onHandleAttributeChanged = (attributeName, attributeValue) => {
        if (attributeName === FieldNames.projectFields.name) {
            setIsSaveDisabled(true);
            setIsNameVerified(FieldNames.yesNo.no);
            setSaveDisabledMessage('Please verify name before saving');
        }

        if (project?.attributes) {
            const updatedProject = {
                ...project,
                attributes: {
                    ...project.attributes,
                    [attributeName]: attributeValue !== '' ? attributeValue : null,
                },
                geometry: project.geometry ?? project.perimeter ?? null,
            }
            dispatch(setProject(updatedProject));
        }
    };

    const contentsByTab = (detail) => {
        let updatedDetail = JSON.parse(JSON.stringify(detail));
        return (
            <div className='details-panel'>
                {updatedDetail.fields.map((field, i) =>
                    field && field.substring(0, 3) === '*H*' ? (
                        <div key={`${field} - ${i} - Subheader`}>
                            <Typography variant='h5' sx={{textDecoration: 'underline'}}>
                                {field.substring(3)}
                            </Typography>
                        </div>
                    ) : (
                        <DetailsGridProjectItem
                            key={`${field} - ${i} - Field`}
                            field={field}
                            onFieldChange={onHandleAttributeChanged}
                            verifyName={verifyName}
                            isNameVerified={isNameVerified}
                            requiredFields={requiredFields}
                        />
                    )
                )}
                {updatedDetail.identifier === FieldNames.panels.project && (
                    <Grid container>
                        <Grid size={6}>
                            <Tooltip
                                title={
                                    project?.attributes &&
                                    project.attributes[FieldNames.projectFields.id]
                                        ? ''
                                        : 'Save first to edit'
                                }
                            >
                                <span>
                                    <Button
                                        color='primary'
                                        variant='contained'
                                        style={{marginTop: 5}}
                                        disabled={
                                            project?.attributes &&
                                            project.attributes[FieldNames.projectFields.id]
                                                ? false
                                                : true
                                        }
                                        onClick={handleNewPostFireTreatmentAction}
                                    >
                                        Create New Post Fire Treatment
                                    </Button>
                                </span>
                            </Tooltip>
                        </Grid>

                        <Grid size={6}>
                            <Tooltip
                                title={
                                    project?.attributes &&
                                    project.attributes[FieldNames.projectFields.id]
                                        ? ''
                                        : 'Save first to edit'
                                }
                            >
                                <span>
                                    <Button
                                        color='primary'
                                        variant='contained'
                                        style={{marginTop: 5}}
                                        disabled={
                                            project?.attributes &&
                                            project.attributes[FieldNames.projectFields.id]
                                                ? false
                                                : true
                                        }
                                        onClick={handleNewPostFireActivityAction}
                                    >
                                        Create New Post Fire Activity
                                    </Button>
                                </span>
                            </Tooltip>
                        </Grid>
                    </Grid>
                )}
                {updatedDetail.identifier === FieldNames.panels.linked && (
                    <List
                        sx={{
                            height: '100%',
                            overflowY: 'hidden',
                            paddingTop: 0,
                            paddingBottom: 0,
                        }}
                        component='div'
                        aria-labelledby='nested-list'
                    >
                        {projectLinkedFeatures.map((eaAction, i) => (
                            <ListItem
                                key={eaAction[FieldNames.featureFields.id]}
                                disableGutters
                                disablePadding
                                style={{
                                    borderBottom: '1px solid',
                                    borderBottomColor: ColorConstants.BlueAlien,
                                }}
                            >
                                {isLinkedQuerying ? (
                                    <CircularProgress size={20}/>
                                ) : (
                                    <ListItemButton
                                        component={Link}
                                        to={handleClickOfLinkedAction(eaAction)}
                                    >
                                        <ListItemIcon>
                                            <Avatar
                                                sx={{
                                                    width: 25,
                                                    height: 25,
                                                    margin: '0 2px 0 0',
                                                    padding: 0,
                                                    backgroundColor: ColorConstants.AvatarGray,
                                                }}
                                            >
                                                {eaAction[FieldNames.featureFields.class] ===
                                                FieldNames.classTypes.treatment ||
                                                eaAction[FieldNames.featureFields.class] ===
                                                FieldNames.classTypes.actualTreatment
                                                    ? 'T'
                                                    : 'A'}
                                            </Avatar>
                                        </ListItemIcon>
                                        <ListItemText
                                            sx={{padding: 0, margin: 0}}
                                            primary={eaAction[FieldNames.featureFields.name]}
                                        />
                                        <ListItemIcon
                                            style={{minWidth: 30, padding: 0, margin: 0}}
                                        >
                                            <ArrowForwardIosIcon
                                                fontSize='small'
                                                style={{
                                                    color: 'gray',
                                                    height: 30,
                                                    margin: '0 0px 0 0',
                                                }}
                                            />
                                        </ListItemIcon>
                                    </ListItemButton>
                                )}
                            </ListItem>
                        ))}
                    </List>
                )}
            </div>
        );
    };

    const getMissingFieldsCountByGroup = (feature, detail) => {
        let count = 0;
        let remainingReqFields = [];
        if (feature?.attributes && detail) {
            const missingFields = getAllMissingFields(feature, false, false, true);
            if (
                detail.fields &&
                missingFields &&
                detail.fields.length > 0 &&
                missingFields.length > 0
            ) {
                detail.fields.forEach((element) => {
                    if (missingFields.includes(element)) {
                        remainingReqFields.push(element);
                    }
                });
            } else {
                remainingReqFields = [];
            }
            count = remainingReqFields.length;
        }
        return count <= 0 ? 0 : count;
    };

    const getUnsavedFieldsCountByGroup = (feature, detail) => {
        let count = 0;
        let fieldsUnsavedInGroup = [];
        let unsavedFields = projectValidationUnsavedValues;
        if (feature && detail) {
            for (let prop in unsavedFields) {
                if (unsavedFields[prop] === false) {
                    continue;
                } else {
                    if (detail && detail.fields && detail.fields.length > 0) {
                        if (detail.fields.includes(prop)) {
                            fieldsUnsavedInGroup.push(prop);
                        }
                    }
                }
            }
            count = fieldsUnsavedInGroup.length;
        }
        return count <= 0 ? 0 : count;
    };

    const isInGroup = (detail, item) => {
        let isInCurrentGroup = false;
        if (item && detail && item.attributes) {
            const requiredFields = getRequiredFields(item, false, null, false, true);
            if (
                detail.fields &&
                requiredFields &&
                detail.fields.length > 0 &&
                requiredFields.length > 0
            ) {
                detail.fields.forEach((element) => {
                    if (requiredFields.includes(element)) {
                        isInCurrentGroup = true;
                    }
                });
            }
        }
        return isInCurrentGroup;
    };

    const chipClicked = () => {
        const groupings = FormGroupings.projectDetails;
        let itemsToOpen = [];
        let updatedValue = {};
        groupings.forEach((element) => {
            const count = getMissingFieldsCountByGroup(project, element);
            if (count > 0) {
                updatedValue[element.identifier] = 'true';
                itemsToOpen.push(element.identifier);
            } else {
                updatedValue[element.identifier] = 'false';
            }
        });

        setExpandedItems((expandedItems) => ({
            ...expandedItems,
            ...updatedValue,
        }));
    };

    const chipClickedUnsaved = () => {
        let groupings = FormGroupings.projectDetails;
        let itemsToOpen = [];
        let updatedValue = {};
        groupings.forEach((element) => {
            const count = getUnsavedFieldsCountByGroup(project, element);
            if (count > 0) {
                updatedValue[element.identifier] = 'true';
                itemsToOpen.push(element.identifier);
            } else {
                updatedValue[element.identifier] = 'false';
            }
        });

        setExpandedItems((expandedItems) => ({
            ...expandedItems,
            ...updatedValue,
        }));
    };

    const getAccordionContent = (detail) => {
        return (
            <div
                className={
                    detail.identifier === 'trackingPanel'
                        ? 'incident-details-content last'
                        : 'incident-details-content'
                }
                key={detail.identifier}
            >
                <Accordion
                    className='incident-details-panel'
                    expanded={expandedItems[detail.identifier] === 'true' ? true : false}
                    defaultExpanded={true}
                    style={{marginTop: 10}}
                >
                    <AccordionSummary
                        id={detail.identifier}
                        className='incident-details-summary'
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleExpandedChanged(e, detail.identifier, expandedItems, setExpandedItems)
                        }}
                        style={{zIndex: 10}}
                        expandIcon={<ExpandMoreIcon/>}
                    >
                        {title(detail)}
                        {isInGroup(detail, feature) &&
                        getUnsavedFieldsCountByGroup(feature, detail) > 0 &&
                        mode === FieldNames.modes.edit ? (
                            <DetailsEdited
                                unsavedFields={getUnsavedFieldsCountByGroup(feature, detail)}
                            />
                        ) : (
                            ''
                        )}
                        {isInGroup(detail, feature) &&
                        getMissingFieldsCountByGroup(feature, detail) > 0 &&
                        mode === FieldNames.modes.edit ? (
                            <DetailsRequired
                                missingFields={getMissingFieldsCountByGroup(feature, detail)}
                            />
                        ) : (
                            ''
                        )}
                    </AccordionSummary>
                    <AccordionDetails>
                        {detail && contentsByTab(detail)}
                        {detail.identifier === FieldNames.panels.tracking ? (
                            <Box sx={{paddingTop: '10px !important'}}>
                                <Accordion>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon/>}
                                        aria-controls='panel1a-content'
                                        id='panel1a-header'
                                    >
                                        <Typography>View History</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box
                                            sx={{
                                                height: '250px',
                                                width: '100%',
                                                minWidth: '10px',
                                            }}
                                        >
                                            <DataGridPremium
                                                rows={featureHistoryRows}
                                                columns={featureHistoryCols}
                                            ></DataGridPremium>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        ) : null}
                    </AccordionDetails>
                </Accordion>
            </div>
        );
    };

    const getListPanel = () => {
        const tempDetails = groupDetails?.projectDetails;
        return (
            <Box
                className='incident-list-inner'
                style={{height: '100%', overflowY: 'scroll', overflowX: 'hidden'}}
            >
                {tempDetails.map((detail) => getAccordionContent(detail))}
            </Box>
        );
    };

    const onSaveChanges = async () => {
        try {
            dispatch(setShowGlobalLoader(true));
            setIsSaveDisabled(true);
            let tempEditableProject = {};

            tempEditableProject.attributes = _.cloneDeep(project?.attributes)
            tempEditableProject.geometry = project.geometry.clone()

            // 'isNameVerified' should always be true at this stage. 'Save' button is disabled until verified
            // This is a double-guard to protect against UI slip-through
            if (isNameVerified === FieldNames.yesNo.yes) {
                tempEditableProject.attributes[FieldNames.projectFields.verify] = FieldNames.yesNo.yes;
            } else {
                new Error('Project name is not verified');
            }

            if (tempEditableProject?.attributes) {
                //These items are the same whether it's a new project or we are just updating a project
                let perimeter = tempEditableProject.geometry;
                if (!perimeter) {
                    perimeter = tempEditableProject.perimeter;
                }

                if (userInfo) {
                    tempEditableProject.attributes[FieldNames.projectFields.lastModifiedDate] =
                        getSaveableDate(new Date());
                    tempEditableProject.attributes[FieldNames.projectFields.lastModifiedBy] =
                        userInfo[FieldNames.userFields.nifcUsername];
                }

                if (isNewFeature) {
                    if (userInfo) {
                        tempEditableProject.attributes[FieldNames.projectFields.createdBy] =
                            userInfo[FieldNames.userFields.nifcUsername];
                        tempEditableProject.attributes[FieldNames.projectFields.createdOnDate] =
                            getSaveableDate(new Date());
                    }
                }

                //Differences based on new vs update
                if (tempEditableProject && isNewFeature) // We are creating a feature, not editing one
                {
                    onSaveNewChanges(perimeter, tempEditableProject);
                } else if (tempEditableProject?.attributes) {
                    // We are Editing the Feature, not creating a new one
                    onSaveUpdateChanges(perimeter, tempEditableProject);
                }
            }
        } catch (error) {
            setIsSaveDisabled(false);
            dispatch(setShowGlobalLoader(false));
            ErrorService.addErrorLogEntry(error);
        }
    };

    const onSaveNewChanges = async (perimeter, tempEditableProject) => {
        try {
            if (tempEditableProject?.attributes && isNewFeature) {
                // We are creating a feature, not editing one
                const attributes = tempEditableProject.attributes;
                ProjectService.addProject(perimeter, attributes, userInfo).then((projResp) => {
                    if (projResp.addResults?.[0]) {
                        attributes[FieldNames.projectFields.objectID] =
                            projResp.addResults?.[0]?.objectId;
                    }
                    const date = new Date();
                    const day = date.getDate();
                    const year = date.getFullYear();
                    const month = date.getMonth() + 1;
                    IDService.getProjectID(projResp.addResults?.[0]?.objectId)
                        .then((projectID) => {
                            AuditLogService.addAuditLogEntry(
                                attributes,
                                projectID,
                                FieldNames.joinFeatureValues.project,
                                userInfo[FieldNames.userFields.nifcUsername],
                                [
                                    'Project was created',
                                    ` Project created on ${month}-${day}-${year} by ${userInfo[FieldNames.userFields.nifcUsername]}`,
                                ]
                            )
                                .then((response) => {
                                    setBaseProjectAttributes(_.cloneDeep(attributes));
                                    dispatch(resetProjectValidationState());
                                    dispatch(setShowGlobalLoader(false));
                                    setIsSaveDisabled(false);
                                    tempEditableProject.attributes = attributes;
                                    dispatch(setFeature(tempEditableProject));

                                    dispatch(setProject(tempEditableProject));
                                    dispatch(setIsNewFeature(false));

                                    setSnackbarMessage('Added new Project.');
                                    setSnackbarSeverity('success');
                                    setSnackbarOpen(true);
                                    emitCustomEvent(Events.ClearModifiedLayer);
                                    setBaseProjectAttributes(
                                        _.cloneDeep(tempEditableProject.attributes)
                                    );
                                    const isPostFire = true; // Alter to below when Fuels gets projects
                                    const entityStringValue = isPostFire ? 'pf' : 'f';
                                    navigate(
                                        `/${entityStringValue}/projects/${projResp.addResults?.[0]?.objectId}`
                                    );
                                    emitCustomEvent(Events.RefreshLayer, {
                                        geo: perimeter,
                                        geometryType: FieldNames.joinFeatureValues.polygon,
                                        isPostFire: true,
                                        featureClass: FieldNames.classTypes.project,
                                        attributes: tempEditableProject.attributes,
                                        isProject,
                                    });
                                })
                                .catch((error) => {
                                    ErrorService.addErrorLogEntry(
                                        error
                                    );
                                    dispatch(setShowGlobalLoader(false));
                                    setIsSaveDisabled(false);
                                    setSnackbarMessage('An error has occurred');
                                    setSnackbarSeverity('error');
                                });
                        })
                        .catch((error) => {
                            ErrorService.addErrorLogEntry(
                                error
                            );
                            dispatch(setShowGlobalLoader(false));
                            setIsSaveDisabled(false);
                            setSnackbarSeverity('error');
                            setSnackbarMessage('An error has occurred');
                        });
                });
            }
        } catch (error) {
            setIsSaveDisabled(false);
            dispatch(setShowGlobalLoader(false));
            ErrorService.addErrorLogEntry(error);
        }
    };

    useEffect(() => {
        if (baseProjectAttributes === null || project === null) {
            return;
        }

        haveProjectValuesChanged(false);
    }, [baseProjectAttributes, project]);

    const haveProjectValuesChanged = (isFromFunding) => {
        if (baseProjectAttributes === null || project === null) {
            return false;
        }
        dispatch(resetProjectValidationState());
        const isNewCheck = baseProjectAttributes[FieldNames.featureFields.isNew] && isNewFeature;
        for (let prop in project.attributes) {
            const editableProp = project.attributes[prop];
            const baseProp = baseProjectAttributes[prop];

            // Note: We are using soft equality to catch instances where numbers are stored as strings in the db (I.E. Latitude/Longitude)
            if (
                (isNewCheck === true && editableProp) ||
                (baseProp && editableProp && baseProp != editableProp) ||
                (!baseProp && editableProp) ||
                (baseProp && !editableProp)
            ) {
                if (prop === FieldNames.featureFields.wbsID) {
                    if (
                        project.attributes[FieldNames.featureFields.fundingAgency]?.includes(
                            FieldNames.agencies.blm
                        )
                    ) {
                        const appendedValue = FieldNames.wbsPrefixes.blm + editableProp + '0000';
                        if (appendedValue === baseProp) {
                            dispatch(genericProjectValidationFieldSetFalse(prop));
                        } else {
                            dispatch(genericProjectValidationFieldSetTrue(prop));
                        }
                    }
                }
                dispatch(genericProjectValidationFieldSetTrue(prop));
            } else {
                dispatch(genericProjectValidationFieldSetFalse(prop));
            }
        }
    };

    const onSaveUpdateChanges = async (perimeter, tempEditableProject) => {
        try {
            if (
                tempEditableProject?.attributes
            ) // We are Editing the Feature, not creating a new one
            {
                const attributes = tempEditableProject.attributes;
                ProjectService.updateProject(perimeter, attributes, userInfo).then((projResp) => {
                    if (projResp.updateResults?.[0]) {
                        attributes[FieldNames.projectFields.objectID] =
                            projResp.updateResults?.[0]?.objectId;
                    }
                    const date = new Date();
                    const day = date.getDate();
                    const year = date.getFullYear();
                    const month = date.getMonth() + 1;
                    const changeString = getLogForFieldUpdatesOnExistingFeature(
                        projectValidationUnsavedValues
                    );
                    AuditLogService.addAuditLogEntry(
                        attributes,
                        attributes[FieldNames.projectFields.id],
                        FieldNames.joinFeatureValues.project,
                        userInfo[FieldNames.userFields.nifcUsername],
                        [
                            'Project was modified',
                            ` Project was modified on ${month}-${day}-${year} by ${userInfo[FieldNames.userFields.nifcUsername]}. ${changeString}`,
                        ]
                    )
                        .then((response) => {
                            setBaseProjectAttributes(_.cloneDeep(attributes));
                            dispatch(resetProjectValidationState());
                            dispatch(setShowGlobalLoader(false));
                            setIsSaveDisabled(false);
                            tempEditableProject.attributes = attributes;
                            dispatch(setFeature(tempEditableProject));

                            dispatch(setProject(tempEditableProject));
                            dispatch(setIsNewFeature(false));
                            setSnackbarMessage('Project changes saved successfully.');
                            setSnackbarSeverity('success');
                            setSnackbarOpen(true);
                            emitCustomEvent(Events.ClearModifiedLayer);
                            setBaseProjectAttributes(_.cloneDeep(tempEditableProject.attributes));
                            emitCustomEvent(Events.RefreshLayer, {
                                geo: perimeter,
                                geometryType: FieldNames.joinFeatureValues.polygon,
                                isPostFire: true,
                                featureClass: FieldNames.classTypes.project,
                                attributes: tempEditableProject.attributes,
                                isProject,
                            });
                        })
                        .catch((error) => {
                            ErrorService.addErrorLogEntry(
                                error
                            );
                            dispatch(setShowGlobalLoader(false));
                            setIsSaveDisabled(false);
                            setSnackbarMessage('An error has occurred');
                            setSnackbarSeverity('error');
                        });
                });
            }
        } catch (error) {
            setIsSaveDisabled(false);
            dispatch(setShowGlobalLoader(false));
            ErrorService.addErrorLogEntry(error);
        }
    };

    //Is Approved being sent to the header is used to know if we should display the ability to delete. It only needs one approval for delete to be disabled
    return (
        <Box sx={{display: 'flex'}}>
            <CssBaseline/>
            <Drawer variant='permanent' open={true}>
                <div className='incidentList-panel'>
                    <DetailsProjectHeader
                        closeDetail={onClose}
                        disableEdit={disableEdit}
                        disableEditMessage={disableEditMessage}
                        onSaveChanges={onSaveChanges}
                        isSaveDisabled={isSaveDisabled}
                        disableDelete={null}
                        onEditClicked={onHandleEditClicked}
                        saveDisabledMessage={saveDisabledMessage}
                        chipClicked={chipClicked}
                        chipClickedUnsaved={chipClickedUnsaved}
                    />
                </div>
                <div className='incident-list' style={{height: 'calc(100vh - 200px)'}}>
                    {groupDetails &&
                        (groupDetails.treatmentDetails || groupDetails.activityDetails) &&
                        getListPanel()}
                </div>
            </Drawer>
            <div style={{top: 64}}>
                <ThemeProvider theme={theme}>
                    <Snackbar
                        open={snackbarOpen}
                        autoHideDuration={6000}
                        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                        className={classes.snackBarStyle}
                        onClose={() => {
                            setSnackbarOpen(false);
                            setSnackbarMessage('');
                        }}
                    >
                        <Alert
                            onClose={() => {
                                setSnackbarOpen(false);
                                setSnackbarMessage('');
                            }}
                            severity={snackbarSeverity}
                            sx={{width: '100%'}}
                        >
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                </ThemeProvider>
            </div>
        </Box>
    );
}
